import PropTypes from 'prop-types'
import React, { useContext, useEffect, useState } from 'react'

import { twoDigits } from '../../../assets/ts/formatting/formatting'
import useInquiryCount from '../../../hooks/inquiry/use-inquiry-count'
import { UserContext } from '../../../store/user-context-provider'
import LargeContainerKPI from '../kpi-variants/large-container-kpi'
import LargeKPI from '../kpi-variants/large-kpi'
import SmallContainerKPI from '../kpi-variants/small-container-kpi'
import { responsepending, subkpis1 } from './detail-kpis-data'

interface KPIs {
  colorcode: number
  count: string
  link: string
  title: string
}

interface Props {
  allCount: string
  filter: any
  kpiTitle: any
  sectionCode: number
  setKpiName: any
  updateKpiFilter
}
const DetailKPIs = ({
  allCount,
  filter,
  kpiTitle,
  sectionCode,
  setKpiName,
  updateKpiFilter,
}: Props) => {
  const userContext = useContext(UserContext)
  const currentUser = userContext?.me?.rid ?? ''

  const determineSectionCode = (code) => {
    if (code === -1 || code === sectionCode) {
      return sectionCode
    } else {
      return '00'
    }
  }
  const { inquiryCount: unAssignedCount } = useInquiryCount({
    ...filter,
    activeFilter:  filter?.tabTable === 'ACTIVE' ? true : null, // The intention of the comparison is to make unAssignedCount impossible to return a number > 0 since an inquiry can't be both active and unassigned at the same time.
    assignedToMe: null,
    assignedToOther:null,
    currentSection:undefined,
    customerActionRequiredFilter: null,
    elevatedToMPF: null,
    isCPOwnerOrLeadership: null,
    isResponsePending: undefined,
    isResponseRequired: undefined,
    needToWorkFilter: null,
    pendingActionOutsideFilter: null,
    returnedFromMPF: null,
    sectionCodeKPIFilter: sectionCode,
    statusCodeFilter: '',
    unassignedFilter: true,
  })
  const { inquiryCount: activeCount } = useInquiryCount({
    ...filter,
    activeFilter: true,
    assignedToMe: null,
    assignedToOther: null,
    currentSection: undefined,
    customerActionRequiredFilter: null,
    elevatedToMPF: null,
    isCPOwnerOrLeadership: null,
    isResponsePending: null,
    isResponseRequired: null,
    needToWorkFilter: null,
    pendingActionOutsideFilter: null,
    returnedFromMPF: null,
    sectionCodeKPIFilter: sectionCode,
    statusCodeFilter: '',
    unassignedFilter: filter?.tabTable === 'UNASSIGNED' ? true : null, // The intention of the comparison is to make activeCount impossible to return a number > 0 since an inquiry can't be both active and unassigned at the same time.
  })
  const { inquiryCount: assignedToMeCount } = useInquiryCount({
    ...filter,
    activeFilter: true,
    assignedToMe: currentUser,
    assignedToOther: null,
    currentSection: undefined,
    customerActionRequiredFilter: null,
    elevatedToMPF: null,
    isCPOwnerOrLeadership: null,
    isResponsePending: null,
    isResponseRequired: null,
    needToWorkFilter: null,
    pendingActionOutsideFilter: null,
    returnedFromMPF: null,
    sectionCodeKPIFilter: sectionCode,
    statusCodeFilter: '',
    unassignedFilter: filter?.tabTable === 'UNASSIGNED' ? true : null, // The intention of the comparison is to make activeCount impossible to return a number > 0 since an inquiry can't be both active and unassigned at the same time.
  })
  const { inquiryCount: assignedToMeNTWCount } = useInquiryCount({
    ...filter,
    activeFilter: true,
    assignedToMe: currentUser,
    assignedToOther: null,
    currentSection: undefined,
    customerActionRequiredFilter: null,
    elevatedToMPF: null,
    isCPOwnerOrLeadership: null,
    isResponsePending: undefined,
    isResponseRequired: undefined,
    needToWorkFilter: true,
    pendingActionOutsideFilter: null,
    returnedFromMPF: null,
    sectionCodeKPIFilter: sectionCode,
    statusCodeFilter: '',
    unassignedFilter: filter?.tabTable === 'UNASSIGNED' ? true : null, // The intention of the comparison is to make activeCount impossible to return a number > 0 since an inquiry can't be both active and unassigned at the same time.
  })
  const { inquiryCount: assignedToMeCARCount } = useInquiryCount({
    ...filter,
    activeFilter: true,
    assignedToMe: currentUser,
    assignedToOther: null,
    currentSection: undefined,
    customerActionRequiredFilter: true,
    elevatedToMPF: null,
    isCPOwnerOrLeadership: undefined,
    isResponsePending: undefined,
    isResponseRequired: undefined,
    needToWorkFilter: null,
    pendingActionOutsideFilter: false,
    returnedFromMPF: null,
    sectionCodeKPIFilter: sectionCode,
    statusCodeFilter: '',
    unassignedFilter: filter?.tabTable === 'UNASSIGNED' ? true : null, // The intention of the comparison is to make activeCount impossible to return a number > 0 since an inquiry can't be both active and unassigned at the same time.
  })
  const { inquiryCount: assignedToMePAOCount } = useInquiryCount({
    ...filter,
    activeFilter: true,
    assignedToMe: currentUser,
    assignedToOther: null,
    currentSection: undefined,
    customerActionRequiredFilter: false,
    elevatedToMPF: null,
    isCPOwnerOrLeadership: undefined,
    isResponsePending: undefined,
    isResponseRequired: undefined,
    needToWorkFilter: null,
    pendingActionOutsideFilter: true,
    returnedFromMPF: null,
    sectionCodeKPIFilter: sectionCode,
    statusCodeFilter: '',
    unassignedFilter: filter?.tabTable === 'UNASSIGNED' ? true : null, // The intention of the comparison is to make activeCount impossible to return a number > 0 since an inquiry can't be both active and unassigned at the same time.
  })
  const { inquiryCount: assignedToOtherCount } = useInquiryCount({
    ...filter,
    activeFilter: true,
    assignedToMe: null,
    assignedToOther: currentUser,
    currentSection: undefined,
    customerActionRequiredFilter: null,
    elevatedToMPF: null,
    isCPOwnerOrLeadership: null,
    isResponsePending: null,
    isResponseRequired: null,
    needToWorkFilter: null,
    pendingActionOutsideFilter: null,
    returnedFromMPF: null,
    sectionCodeKPIFilter: sectionCode,
    statusCodeFilter: '',
    unassignedFilter: filter?.tabTable === 'UNASSIGNED' ? true : null, // The intention of the comparison is to make activeCount impossible to return a number > 0 since an inquiry can't be both active and unassigned at the same time.
  })

  const { inquiryCount: assignedToOtherNTWCount } = useInquiryCount({
    ...filter,
    activeFilter: true,
    assignedToMe: null,
    assignedToOther:currentUser ,
    currentSection:undefined,
    customerActionRequiredFilter: null,
    elevatedToMPF: null,
    isCPOwnerOrLeadership:null,
    isResponsePending: undefined,
    isResponseRequired: undefined,
    needToWorkFilter:true,
    pendingActionOutsideFilter: null,
    returnedFromMPF: null,
    sectionCodeKPIFilter: sectionCode,
    statusCodeFilter: '',
    unassignedFilter: filter?.tabTable === 'UNASSIGNED' ? true : null, // The intention of the comparison is to make activeCount impossible to return a number > 0 since an inquiry can't be both active and unassigned at the same time.
  })
  const { inquiryCount: assignedToOtherCARCount } = useInquiryCount({
    ...filter,
    activeFilter: true,
    assignedToMe: null,
    assignedToOther: currentUser,
    currentSection: undefined,
    customerActionRequiredFilter: true,
    elevatedToMPF: null,
    isCPOwnerOrLeadership: undefined,
    isResponsePending: undefined,
    isResponseRequired: undefined,
    needToWorkFilter: null,
    pendingActionOutsideFilter: false,
    returnedFromMPF: null,
    sectionCodeKPIFilter: sectionCode,
    statusCodeFilter: '',
    unassignedFilter: filter?.tabTable === 'UNASSIGNED' ? true : null, // The intention of the comparison is to make activeCount impossible to return a number > 0 since an inquiry can't be both active and unassigned at the same time.
  })
  const { inquiryCount: assignedToOtherPAOCount } = useInquiryCount({
    ...filter,
    activeFilter: true,
    assignedToMe: null,
    assignedToOther: currentUser,
    currentSection: undefined,
    customerActionRequiredFilter: false,
    elevatedToMPF: null,
    isCPOwnerOrLeadership: undefined,
    isResponsePending: undefined,
    isResponseRequired: undefined,
    needToWorkFilter: null,
    pendingActionOutsideFilter: true,
    returnedFromMPF: null,
    sectionCodeKPIFilter: sectionCode,
    statusCodeFilter: '',
    unassignedFilter: filter?.tabTable === 'UNASSIGNED' ? true : null, // The intention of the comparison is to make activeCount impossible to return a number > 0 since an inquiry can't be both active and unassigned at the same time.
  })

  const [data, setData] = useState<KPIs[]>([])
  useEffect(() => {
    if (kpiTitle === 'subkpis1') {
      setData(subkpis1)
    }
    if (kpiTitle === 'response pending') {
      setData(responsepending)
    }
  }, [kpiTitle])


  return (
    <div className='row w-100 m-0 '>
      <div
        className='col col-md-12 align-items-stretch d-flex mt-1'
      >
        <div className='bordered2 border-radius w-100 p-0'>
          <LargeKPI
          filter={filter}
            key={0}
            kpi={{
              colorcode: 2,
              count: allCount,
              sectionCode: determineSectionCode(sectionCode),
              title: 'All',
            }}
            setKpiName={setKpiName}
            updateKpiFilter={updateKpiFilter}
          />
        </div>
      </div>
      <SmallContainerKPI
        filter={filter}
        mainKPI={{
          colorcode: 3,
          count: twoDigits(unAssignedCount[0]?.InquiryGUID_count) || '00',
          sectionCode: determineSectionCode(sectionCode),
          title: 'Unassigned',
        }}
        sectionCode={sectionCode}
        setKpiName={setKpiName}
        smallKPIData={data}
        updateKpiFilter={updateKpiFilter}
      />
      <LargeContainerKPI
        bgcNums={[43, 44, 45]}
        mainKPI={{
          colorcode: 6,
          count: twoDigits(activeCount[0]?.InquiryGUID_count) || '00',
          sectionCode: determineSectionCode(sectionCode),
          title: 'Active',
        }}
        mainKPIData={[
          {
            colorcode: 7,
            count: twoDigits(assignedToMeCount?.[0]?.InquiryGUID_count) || '00',
            sectionCode: determineSectionCode(sectionCode),
            title: 'Assigned to me',
          },
          {
            colorcode: 11,
            count: twoDigits(assignedToOtherCount[0]?.InquiryGUID_count) || '00',
            sectionCode: determineSectionCode(sectionCode),
            title: 'Assigned to Others',
          },
        ]}
        mediumKPIData={[
          [
            {
              colorcode: 8,
              count: twoDigits(assignedToMeNTWCount[0]?.InquiryGUID_count) || '00',
              sectionCode: determineSectionCode(sectionCode),
              title: 'Need To Work',
            },
            {
              colorcode: 9,
              count: twoDigits(assignedToMeCARCount[0]?.InquiryGUID_count) || '00',
              sectionCode: determineSectionCode(sectionCode),
              title: 'Customer Action Required',
            },
            {
              colorcode: 10,
              count: twoDigits(assignedToMePAOCount[0]?.InquiryGUID_count) || '00',
              sectionCode: determineSectionCode(sectionCode),
              title: 'Pending Action Outside CP',
            },
          ],
          [
            {
              colorcode: 8,
              count: twoDigits(assignedToOtherNTWCount[0]?.InquiryGUID_count) || '00',
              sectionCode: determineSectionCode(sectionCode),
              title: 'Need To Work',
            },
            {
              colorcode: 9,
              count: twoDigits(assignedToOtherCARCount[0]?.InquiryGUID_count) || '00',
              sectionCode: determineSectionCode(sectionCode),
              title: 'Customer Action Required',
            },
            {
              colorcode: 10,
              count: twoDigits(assignedToOtherPAOCount[0]?.InquiryGUID_count) || '00',
              sectionCode: determineSectionCode(sectionCode),
              title: 'Pending Action Outside CP',
            },
          ],
        ]}
        setKpiName={setKpiName}
        updateKpiFilter={updateKpiFilter}
      />
    </div>
  )
}
DetailKPIs.propTypes = {
  kpiTitle: PropTypes.string,
}
export default DetailKPIs
